import { useCallback } from 'react'
import { useGetUserSession, useSignOut } from '@/shared/hooks/api/auth'
import {
  useCurrentOrganization,
  useRecordContactSalesInterest,
} from '@/shared/hooks/api/organization'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AuthLayout } from '@/features/auth/auth-layout'

export function TrialAccessBlocked() {
  const { t } = useTranslation('auth')
  const { data: session } = useGetUserSession()
  const { data: organization } = useCurrentOrganization()
  const {
    mutateAsync: recordContactSales,
    isPending: isRecordingContactSales,
  } = useRecordContactSalesInterest()
  const { mutate: signOut, isPending: isSigningOut } = useSignOut()

  const hasContactedSales = Boolean(organization?.contactSalesRequestedAt)

  const handleContactSales = useCallback(async () => {
    const organizationId = session?.organizationId
    if (!organizationId) {
      return
    }
    try {
      await recordContactSales(organizationId)
    } catch {
      toast.error(t('trialExpired.contactSalesError'))
    }
  }, [recordContactSales, session?.organizationId, t])

  const handleSignOut = useCallback(() => {
    signOut()
  }, [signOut])

  return (
    <AuthLayout>
      <Card className='gap-4'>
        <CardHeader>
          <CardTitle className='text-lg tracking-tight'>
            {hasContactedSales
              ? t('trialExpired.salesContactReceivedTitle')
              : t('trialExpired.title')}
          </CardTitle>
          <CardDescription>
            {hasContactedSales
              ? t('trialExpired.salesContactReceivedDescription')
              : t('trialExpired.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          {!hasContactedSales && (
            <Button
              type='button'
              disabled={isRecordingContactSales || !session?.organizationId}
              onClick={handleContactSales}
            >
              {t('trialExpired.contactSales')}
            </Button>
          )}
          <Button
            type='button'
            variant='outline'
            disabled={isSigningOut}
            onClick={handleSignOut}
          >
            {t('trialExpired.signOut')}
          </Button>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
